/**
 * CodeCell — inline-editable monospace cell for a single Xactimate CAT or
 * SEL token in the flagship estimate-detail grid.
 *
 * Behaviour mirrors the prototype's `CodeCell` (design_reference/js/
 * screens-detail.jsx) but edits are **session-local only**: there is no
 * per-line scope endpoint (PRP Gotcha #4). A click reveals a small editable
 * `Input`; Enter/blur commits to the parent-held override map, Esc cancels.
 * CAT renders in `var(--accent-ink)`.
 */

import { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { IconChevd } from './icons';
import { cn } from '../../lib/utils';

/** Props for {@link CodeCell}. */
export interface CodeCellProps {
  /** Current displayed value (already resolved through the override map). */
  value: string;
  /** Which token this cell edits — affects accent color and a11y label. */
  field: 'cat' | 'sel';
  /**
   * Commit handler. Called with the trimmed, upper-cased new value when the
   * user presses Enter or blurs with a changed value.
   *
   * NOTE: edits are session-local. The page holds them in an override map.
   * // TODO: persist via BackendAPI.saveProjectScope
   */
  onCommit: (next: string) => void;
}

/**
 * Renders an inline-editable CAT/SEL code cell.
 *
 * @param props - {@link CodeCellProps}.
 * @returns The editable cell element.
 */
export function CodeCell({ value, field, onCommit }: CodeCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reason: keep the draft in sync if an external override changes the value.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    const next = draft.trim().toUpperCase();
    setEditing(false);
    if (next !== value) onCommit(next);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={draft}
        aria-label={field === 'cat' ? 'Category code' : 'Selector code'}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        className={cn(
          'h-[22px] w-[72px] px-[6px] font-[var(--font-mono)] text-[11px]',
          'border-[var(--accent)] shadow-[0_0_0_2px_var(--accent-soft)]',
        )}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className={cn(
        'inline-flex items-center gap-1 h-[22px] px-[6px] rounded-[var(--r-xs)]',
        'border border-[var(--bd)] bg-[var(--bg-2)] font-[var(--font-mono)] text-[11px]',
        'tracking-[.02em] outline-none',
        'hover:border-[var(--bd-strong)] hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)]',
        field === 'cat' ? 'text-[var(--accent-ink)]' : 'text-[var(--tx-mid)]',
      )}
    >
      {value || '—'}
      <IconChevd size={11} className="text-[var(--tx-faint)]" />
    </button>
  );
}
